import { Injectable } from '@angular/core';

import { Router } from '@angular/router';
import {Account, AccountUser} from 'src/models/account.model';

import firebase from 'firebase/app';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, AngularFirestoreDocument, AngularFirestoreCollection, DocumentReference } from '@angular/fire/firestore';

import {Observable, of} from "rxjs";
import {map, take} from "rxjs/operators";
import {User} from "../models/user.model";
import {AuditService} from "./audit.service";
import {AuditActionType} from "../models/audit.model";

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private accountUsers: Observable<AccountUser[]>;
  private accountUserCollection: AngularFirestoreCollection<AccountUser>;

  private accounts: Observable<Account[]>;
  private accountCollection: AngularFirestoreCollection<Account>;

  public accountId: any;

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private auditService: AuditService,
  ) {
    this.initializeAccounts();
    this.initializeAccountUsers();
  }


  //====> ACCOUNTS ==========//

  getAccountById(id: string): Observable<Account>{

    this.accountCollection = this.afs.collection<Account>('accounts');
    return this.accountCollection.doc<Account>(id).valueChanges().pipe(
      take(1),
      map(account =>{
        account.id = id;
        return account;
      })
    );

  }

  initializeAccounts(){
    this.accountCollection = this.afs.collection<Account>('accounts');
    this.accounts = this.accountCollection.snapshotChanges().pipe(
      map(actions =>{
        return actions.map(a =>{
          const data = a.payload.doc.data();
          const id = a.payload.doc.id;
          return { id, ...data };
        })
      }));
  }

  initializeAccountById(id: string) {
    if (id) {
      this.getAccountById(id).subscribe(account => {
        console.log(account);
        return account
      });
    }
  }

  getAccounts():Observable<Account[]> {
    return this.accounts;
  }

  async addAccount(account: Account): Promise<DocumentReference<Account>> {
    const result = await this.accountCollection.add(account);

    // Log account creation
    await this.auditService.logCRUD(
      AuditActionType.CREATE,
      'account',
      result.id,
      account.account_name,
      null,
      account
    );

    return result;
  }

  async updateAccount(account: Account, id) {
    // Get current account data for audit logging
    const currentAccount = await this.getAccountById(id).pipe(take(1)).toPromise();

    const result = await this.afs
      .collection("accounts")
      .doc(id)
      .update(account);

    // Log account update
    await this.auditService.logCRUD(
      AuditActionType.UPDATE,
      'account',
      id,
      account.account_name || currentAccount?.account_name,
      currentAccount,
      account
    );

    return result;
  }

  updateAccountAddress(address: any, account_id: string): Promise<void> {
    return this.afs
      .collection("accounts")
      .doc(account_id)
      .set(address, { merge: true });
  }

  updateAccountVehicles(vehicles: any[], account_id: string): Promise<void> {
    return this.afs
      .collection("accounts")
      .doc(account_id)
      .set({ vehicles: vehicles }, { merge: true });
  }

  async deleteAccount(account: Account) {
    const result = await this.afs
      .collection("accounts")
      .doc(account.uid)
      .delete();

    // Log account deletion
    await this.auditService.logCRUD(
      AuditActionType.DELETE,
      'account',
      account.uid,
      account.account_name,
      account,
      null
    );

    return result;
  }

  checkIfUserIsOwner(id: string):Promise<any>{
    return new Promise((resolve, reject) => {
      const query = firebase.firestore().collection('accounts');
      const observer = query
        .where('owner_id', '==', id)
        .limit(1)
        .get();

      observer.then((snapshot) => {
        if (snapshot.empty) {
          console.log("Sorry, user has no account. Please contact your administrator.")
          resolve(false);
          return;
        }

        snapshot.forEach((doc) =>{
          console.log("doc.data() ==>", doc.data());
          let account = doc.data();
          account.account_id = doc.id;
          resolve(account);
        });

      });

    });
  }

  getAccountByKey(account_key: string):Promise<any>{
    return new Promise((resolve, reject) => {
      const query = firebase.firestore().collection('accounts');
      query
        .where("account_key", "==", account_key)
        .limit(1)
        .get().then((snapshots) =>{

        if(snapshots.empty){
          return;
        }

        snapshots.forEach((doc) =>{
          let account = doc.data();
          account.account_id = doc.id;
          resolve(account);
        });

      });
    });
  }

  // Extract unique vehicle types from account settings
  getVehicleTypesFromAccount(account: any, zoneId?: string): Observable<any[]> {
    if (!account || !account.settings || !account.settings.zones) {
      return of([]);
    }

    const uniqueVehicleTypes = new Map();
    
    // Filter zones by zoneId if provided
    const zones = zoneId 
      ? account.settings.zones.filter(zone => zone.zone_id === zoneId)
      : account.settings.zones;

    // Loop through each zone and collect vehicle types
    zones.forEach(zone => {
      if (zone.customizeRates && Array.isArray(zone.customizeRates)) {
        zone.customizeRates.forEach(rate => {
          if (!uniqueVehicleTypes.has(rate.vehicle_type)) {
            uniqueVehicleTypes.set(rate.vehicle_type, {
              type: rate.vehicle_type,
              name: rate.vehicle_type_custom_name || rate.vehicle_type_name || rate.vehicle_type,
              image: rate.vehicle_image || `assets/img/${rate.vehicle_type}.png`
            });
          }
        });
      }
    });

    return of(Array.from(uniqueVehicleTypes.values()));
  }

  async checkIfUserHasAccount(id: string): Promise<any> {
    try {
      const snapshots = await firebase.firestore().collection('account_users').get();

      if (snapshots.empty) {
        return null;
      }

      // Early exit optimization - find user as soon as we find one
      for (const doc of snapshots.docs) {
        const data = doc.data();
        if (data.users && Array.isArray(data.users)) {
          const foundUser = data.users.find((user: any) => user.id === id);
          if (foundUser) {
            return foundUser;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error checking user account:', error);
      throw error;
    }
  }

  /***** ACCOUNT USER ******/

  getAccountUserById(id: string): Observable<AccountUser>{
    return this.accountUserCollection.doc<AccountUser>(id).valueChanges().pipe(
      take(1),
      map(accountUser =>{
        return accountUser
      })
    );
  }

  initializeAccountUsers(){
    this.accountUserCollection = this.afs.collection<AccountUser>('account_users');
    this.accountUsers = this.accountUserCollection.snapshotChanges().pipe(
      map(actions =>{
        return actions.map(a =>{
          const data = a.payload.doc.data();
          const id = a.payload.doc.id;
          return { id, ...data };
        })
      }));
  }

  initializeAccountUserById(id: string) {
    if (id) {
      this.getAccountUserById(id).subscribe(accountUser => {
        console.log(accountUser);
        return accountUser
      });
    }
  }

  getAccountUsers():Observable<AccountUser[]> {
    return this.accountUsers;
  }

  addAccountUser(accountKey: string, accountUser: any): Promise<void> {
    const userRef = this.afs.doc(`account_users/${accountKey}`);
    return userRef.set({
      users: accountUser
    }, { merge: true });
    //return this.accountUserCollection.add(accountUser);
  }

  updateAccountUser(accountUser: AccountUser, accountKey: string) {
    return this.afs
      .collection("account_users")
      .doc(accountKey)
      .update(accountUser);
  }

  deleteAccountUser(accountUser: AccountUser) {
    return this.afs
      .collection("account_users")
      .doc(accountUser.uid)
      .delete();
  }

  async getDevicesByIds(ids: string[]): Promise<any[]> {
    if (ids.length > 0) {
      const promises = ids.map((device: any) =>
        this.afs.collection('devices').doc(device.uuid).get().toPromise()
      );
      const snapshots = await Promise.all(promises);
      return snapshots.map(snapshot => snapshot.data());
    }
  }

  async disconnectDevice(zoneId: string, deviceUuid: string, accountId: string): Promise<void> {
    try {
      console.log(`Starting disconnect process for device ${deviceUuid} in zone ${zoneId}`);
      
      // Get the current account data
      const accountDoc = await this.afs.collection('accounts').doc(accountId).get().toPromise();
      if (!accountDoc.exists) {
        console.error(`Account document with ID ${accountId} does not exist`);
        throw new Error(`Account not found with ID: ${accountId}`);
      }
      
      const account = accountDoc.data() as Account;
      console.log('Account data retrieved successfully');
      
      if (!account || !account.settings || !account.settings.zones) {
        console.error('Account data is missing settings or zones');
        throw new Error('Account or zones not found');
      }
      
      // Find the zone and update the devices array
      const zoneIndex = account.settings.zones.findIndex(zone => zone.zone_id === zoneId);
      
      if (zoneIndex === -1) {
        console.error(`Zone with ID ${zoneId} not found in account settings`);
        throw new Error('Zone not found');
      }
      
      console.log(`Found zone at index ${zoneIndex}`);
      console.log('Current devices:', account.settings.zones[zoneIndex].devices);
      
      // Check if devices array exists
      if (!account.settings.zones[zoneIndex].devices || !Array.isArray(account.settings.zones[zoneIndex].devices)) {
        console.error('Devices array is missing or not an array');
        account.settings.zones[zoneIndex].devices = [];
        throw new Error('Devices array is missing or invalid');
      }
      
      // Filter out the device to disconnect
      const updatedDevices = account.settings.zones[zoneIndex].devices.filter(
        device => device.uuid !== deviceUuid
      );
      
      console.log(`Updated devices array after filtering: ${updatedDevices.length} devices`);
      
      // Update the zone's devices array
      account.settings.zones[zoneIndex].devices = updatedDevices;
      
      // Update the account document
      console.log('Updating account document...');
      await this.afs.collection('accounts').doc(accountId).update({
        'settings.zones': account.settings.zones
      });
      console.log('Account document updated successfully');
      
      // Check if device exists
      const deviceDoc = await this.afs.collection('devices').doc(deviceUuid).get().toPromise();
      if (!deviceDoc.exists) {
        console.warn(`Device with ID ${deviceUuid} does not exist in devices collection`);
      } else {
        // Update the device status in the devices collection
        console.log('Updating device status...');
        await this.afs.collection('devices').doc(deviceUuid).update({
          zone_id: null,  // Clear zone association
          status: 0       // Set device as inactive/available
        });
        console.log('Device status updated successfully');
      }
      
      console.log('Device disconnection completed successfully');
      return;
    } catch (error) {
      console.error('Error in AccountService.disconnectDevice:', error);
      throw error; // Rethrow to allow handling in the component
    }
  }
}
